import {useEffect, useMemo, useState} from 'react';
import {styled, useTheme} from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CustomerListIconSingle from '../icon/CustomerListIconSingle';
import DataManagementIconSingle from '../icon/DataManagementIconSingle';
import JobIconSingle from '../icon/JobIconSingle';
import TeamIconSingle from '../icon/TeamIconSingle';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import {ButtonColorGradient, PrimaryColor} from '../theme/theme';
import {useLocation, Link as RouterLink} from 'react-router';
import {useAuth} from '../../views/AuthProvider/AuthProvider';
import {Tooltip} from "@mui/material";

export const drawerWidth = 60;
export const drawerPaddingRight = 8;

const openedMixin = (theme) => ({
    backgroundColor: PrimaryColor,
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
    paddingRight: drawerPaddingRight
});

const closedMixin = (theme) => ({
    backgroundColor: PrimaryColor,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: drawerWidth,
    [theme.breakpoints.up('sm')]: {
        width: drawerWidth,
    },
    paddingRight: 8,
    borderRight: 'none',
    boxShadow: '0px 4px 11px 0px #0000001A'
});

const DrawerCollapse = styled('div')(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {shouldForwardProp: (prop) => prop !== 'open'})(
    ({theme, open}) => ({
        boxShadow: '4px 0px 11px 0px #0000001A',
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

export const Navbar = (props) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false);
    const [selectedMenuItem, setSelectedMenuItem] = useState(0);
    const location = useLocation();

    // This function determines if a nav item should be highlighted as "active".
    // It checks if the first part of the current URL path (e.g., "JobTracking" from "/JobTracking/some-id")
    // is present in the list of URLs associated with a navigation item.
    const isRouteActive = useMemo(() => {
        const topLevelRoute = location.pathname.split('/')[1] || '';
        return (routes) => routes.includes(topLevelRoute);
    }, [location.pathname]);

    const [navItems, setNavItems] = useState([
        {id: 'home', label: 'Home', icon: <HomeIcon/>, activeIcon: <HomeIconActive/>, to: '/Home', viewUrls: ['', 'Home', 'OrderCreation', 'JobCreation']},
        // {id: 'orders', label: 'Orders', icon: <OrderIcon/>, activeIcon: <OrderIconActive/>, to: '/Orders', viewUrls: ['Orders', 'OrderTracking', 'OrderManagement', 'CustomerOrders']},
        // {id: 'team', label: 'My Team', icon: <TeamIcon/>, activeIcon: <TeamIconActive/>, to: '/Team', viewUrls: ['Team', 'AllUsers']},
        {id: 'settings', label: 'Settings', icon: <SettingsIcon/>, activeIcon: <SettingsIconActive/>, to: '/Settings', viewUrls: ['Settings']},
        // {id: 'users', label: 'User List', icon: <CompanyIcon/>, activeIcon: <CompanyIconActive/>, to: '/Customers', viewUrls: ['Customers']},
    ]);

    const {features, isFeatureAvailable} = useAuth();

    useEffect(() => {
        let tempNavItems = [];

        if (isFeatureAvailable('admin_dashboard')) {
            tempNavItems.push({id: 'home', label: 'Home', icon: <HomeIcon/>, activeIcon: <HomeIconActive/>, to: '/Home', viewUrls: ['', 'Home', 'OrderCreation', 'JobCreation']});
        } else if (isFeatureAvailable('dashboard')) {
            tempNavItems.push({id: 'home', label: 'Home', icon: <HomeIcon/>, activeIcon: <HomeIconActive/>, to: '/Home', viewUrls: ['', 'Home',]});
        }

        // Show Data Management for admins or for customers who can restore.
        if (isFeatureAvailable('admin_dashboard') || (isFeatureAvailable('dashboard') && isFeatureAvailable('restore'))) {
            tempNavItems.push({id: 'dataManagement', label: 'Data Management', icon: <DataManagementIcon/>, activeIcon: <DataManagementIconActive/>, to: '/DataManagement', viewUrls: ['DataManagement', 'Restore']});
        }

        if (isFeatureAvailable('track')) {
            if (isFeatureAvailable('order')) {
                tempNavItems.push({id: 'jobs', label: 'Jobs', icon: <JobIcon/>, activeIcon: <JobIconActive/>, to: '/Jobs', viewUrls: ['Jobs', 'JobManagement', 'JobTracking']});
            } else {
                tempNavItems.push({id: 'jobs', label: 'Jobs', icon: <JobIcon/>, activeIcon: <JobIconActive/>, to: '/CustomerJobs', viewUrls: ['JobTracking', 'CustomerJobs']});
            }
        }

        if (isFeatureAvailable('user')) {
            if (isFeatureAvailable('admin_dashboard')) {
                tempNavItems.push({id: 'allusers', label: 'Team', icon: <TeamIcon/>, activeIcon: <TeamIconActive/>, to: '/AllUsers', viewUrls: ['AllUsers']});
            } else {
                tempNavItems.push({id: 'team', label: 'My Team', icon: <TeamIcon/>, activeIcon: <TeamIconActive/>, to: '/Team', viewUrls: ['Team']});
            }
        }

        if (isFeatureAvailable('admin_dashboard') && isFeatureAvailable('order')) {
            tempNavItems.push({id: 'customer', label: 'Customer List', icon: <CompanyIcon/>, activeIcon: <CompanyIconActive/>, to: '/Customers', viewUrls: ['Customers']});
        }

        setNavItems(tempNavItems);
    }, [features]);

    const openView = (index, viewUrl) => {
        setSelectedMenuItem(index);
        // Open Route here...
        props.onViewClicked(viewUrl);
    }

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    return (
        <>
            <Drawer variant="permanent" open={open}>
                <Toolbar/>
                <Box sx={{display: 'flex', flexDirection: 'column', height: '100%', flex: 1}}>
                    <List sx={{flex: 1, overflow: 'hidden'}}>
                        {navItems.map((navItem, index) => (

                            <ListItem key={navItem.label} disablePadding sx={{
                                display: 'block',
                                borderRadius: '4px',
                                mx: 0.5,
                                mt: 3,
                                ...(isRouteActive(navItem.viewUrls) && {
                                    background: ButtonColorGradient,
                                }),
                            }}>
                                <Tooltip title={navItem.label} placement="right">
                                    <ListItemButton
                                        onClick={() => openView(index, navItem.to)}
                                        sx={{
                                            justifyContent: open ? 'initial' : 'center',
                                            height: '60px',
                                            px: 2.5,
                                            pt: 5,
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                justifyContent: 'center',
                                                //...(isRouteActive(navItem.viewUrls) && { filter: 'brightness(0)' }),
                                            }}
                                        >
                                            {(isRouteActive(navItem.viewUrls) ? navItem.activeIcon : navItem.icon)}
                                        </ListItemIcon>
                                        <ListItemText primary={navItem.label}
                                                      primaryTypographyProps={{
                                                          style: {
                                                              fontFamily: 'Poppins',
                                                              fontWeight: 400,
                                                              fontSize: '18px',
                                                              color: (isRouteActive(navItem.viewUrls)) ? '#FFFFFF' : '#617388'
                                                          },
                                                      }}
                                                      sx={{opacity: open ? 1 : 0}}/>
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>
                        ))}
                    </List>
                    <Divider sx={{mb: 1}}/>
                    <Box sx={{display: 'flex', justifyContent: 'center', mx: 0.5, mb: 2, flexShrink: 0}}>
                        <Tooltip title={"Settings"} placement="right">
                            <ListItemButton
                                component={RouterLink}
                                to="/Settings"
                                sx={{
                                    justifyContent: 'center',
                                    height: '60px',
                                    px: 2.5,
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    ...(isRouteActive(['Settings']) && {
                                        background: ButtonColorGradient,
                                    }),
                                }}
                                onClick={() => openView(-1, '/Settings')}
                            >
                                <ListItemIcon sx={{minWidth: 0, justifyContent: 'center'}}>
                                    {isRouteActive(['Settings']) ? <SettingsIconActive/> : <SettingsIcon/>}
                                </ListItemIcon>
                            </ListItemButton>
                        </Tooltip>
                    </Box>
                </Box>
                <DrawerCollapse>
                    {/* Toggle Drawer Button */}
                    <IconButton
                        aria-label="toggle drawer"
                        onClick={open ? handleDrawerClose : handleDrawerOpen}
                        edge="start"
                        sx={{marginRight: open ? 5 : 0, color: '#EAEAEA', display: 'none', cursor: 'pointer'}}
                    >
                        {open ? (
                            theme.direction === 'rtl' ? <ChevronRightIcon/> : <ChevronLeftIcon/>
                        ) : (
                            <MenuIcon/>
                        )}
                    </IconButton>
                </DrawerCollapse>
            </Drawer>
        </>
    )
}

export const HomeIcon = () => {
    return (
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_1096_2600)">
                <path d="M14.4007 32V21.4118H21.6007V32H30.6007V18H34.9722L18.0007 2L1.0293 18H5.40073V32H14.4007Z" fill="#CED3D9"/>
            </g>
            <g clipPath="url(#clip1_1096_2600)">
                <path d="M14 32V21.4118H21V32H29.75V18H34L17.5 2L1 18H5.25V32H14Z" fill="#617388"/>
            </g>
            <defs>
                <clipPath id="clip0_1096_2600">
                    <rect width="36" height="36" fill="white"/>
                </clipPath>
                <clipPath id="clip1_1096_2600">
                    <rect width="42" height="16" fill="white" transform="translate(-3 2)"/>
                </clipPath>
            </defs>
        </svg>
    )
};

export const HomeIconActive = () => {
    return (
        <svg width="33" height="30" viewBox="0 0 33 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 30V19.4118H20V30H28.75V16H33L16.5 0L0 16H4.25V30H13Z" fill="#3A3E3F"/>
        </svg>
    )
};

export const OrderIcon = () => {
    return (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_2113_779)">
                <path d="M34 34C32.335 34 31 35.335 31 37C31 37.7956 31.3161 38.5587 31.8787 39.1213C32.4413 39.6839 33.2044 40 34 40C34.7956 40 35.5587 39.6839 36.1213 39.1213C36.6839 38.5587 37 37.7956 37 37C37 35.335 35.65 34 34 34ZM10 10V13H13L18.4 24.385L16.36 28.06C16.135 28.48 16 28.975 16 29.5C16 30.2956 16.3161 31.0587 16.8787 31.6213C17.4413 32.1839 18.2044 32.5 19 32.5H37V29.5H19.63C19.5305 29.5 19.4352 29.4605 19.3648 29.3902C19.2945 29.3198 19.255 29.2245 19.255 29.125C19.255 29.05 19.27 28.99 19.3 28.945L20.65 26.5H31.825C32.95 26.5 33.94 25.87 34.45 24.955L39.82 15.25C39.925 15.01 40 14.755 40 14.5C40 14.1022 39.842 13.7206 39.5607 13.4393C39.2794 13.158 38.8978 13 38.5 13H16.315L14.905 10M19 34C17.335 34 16 35.335 16 37C16 37.7956 16.3161 38.5587 16.8787 39.1213C17.4413 39.6839 18.2044 40 19 40C19.7956 40 20.5587 39.6839 21.1213 39.1213C21.6839 38.5587 22 37.7956 22 37C22 35.335 20.65 34 19 34Z" fill="#CED3D9"/>
            </g>
            <g clipPath="url(#clip1_2113_779)">
                <path d="M34 34C32.335 34 31 35.335 31 37C31 37.7956 31.3161 38.5587 31.8787 39.1213C32.4413 39.6839 33.2044 40 34 40C34.7956 40 35.5587 39.6839 36.1213 39.1213C36.6839 38.5587 37 37.7956 37 37C37 35.335 35.65 34 34 34ZM10 10V13H13L18.4 24.385L16.36 28.06C16.135 28.48 16 28.975 16 29.5C16 30.2956 16.3161 31.0587 16.8787 31.6213C17.4413 32.1839 18.2044 32.5 19 32.5H37V29.5H19.63C19.5305 29.5 19.4352 29.4605 19.3648 29.3902C19.2945 29.3198 19.255 29.2245 19.255 29.125C19.255 29.05 19.27 28.99 19.3 28.945L20.65 26.5H31.825C32.95 26.5 33.94 25.87 34.45 24.955L39.82 15.25C39.925 15.01 40 14.755 40 14.5C40 14.1022 39.842 13.7206 39.5607 13.4393C39.2794 13.158 38.8978 13 38.5 13H16.315L14.905 10M19 34C17.335 34 16 35.335 16 37C16 37.7956 16.3161 38.5587 16.8787 39.1213C17.4413 39.6839 18.2044 40 19 40C19.7956 40 20.5587 39.6839 21.1213 39.1213C21.6839 38.5587 22 37.7956 22 37C22 35.335 20.65 34 19 34Z" fill="#617388"/>
            </g>
            <defs>
                <clipPath id="clip0_2113_779">
                    <rect width="24" height="50" fill="white" transform="translate(26)"/>
                </clipPath>
                <clipPath id="clip1_2113_779">
                    <rect width="26" height="50" fill="white"/>
                </clipPath>
            </defs>
        </svg>
    )
};

export const OrderIconActive = () => {
    return (
        <svg width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_2113_643)">
                <path d="M34 34C32.335 34 31 35.335 31 37C31 37.7956 31.3161 38.5587 31.8787 39.1213C32.4413 39.6839 33.2044 40 34 40C34.7956 40 35.5587 39.6839 36.1213 39.1213C36.6839 38.5587 37 37.7956 37 37C37 35.335 35.65 34 34 34ZM10 10V13H13L18.4 24.385L16.36 28.06C16.135 28.48 16 28.975 16 29.5C16 30.2956 16.3161 31.0587 16.8787 31.6213C17.4413 32.1839 18.2044 32.5 19 32.5H37V29.5H19.63C19.5305 29.5 19.4352 29.4605 19.3648 29.3902C19.2945 29.3198 19.255 29.2245 19.255 29.125C19.255 29.05 19.27 28.99 19.3 28.945L20.65 26.5H31.825C32.95 26.5 33.94 25.87 34.45 24.955L39.82 15.25C39.925 15.01 40 14.755 40 14.5C40 14.1022 39.842 13.7206 39.5607 13.4393C39.2794 13.158 38.8978 13 38.5 13H16.315L14.905 10M19 34C17.335 34 16 35.335 16 37C16 37.7956 16.3161 38.5587 16.8787 39.1213C17.4413 39.6839 18.2044 40 19 40C19.7956 40 20.5587 39.6839 21.1213 39.1213C21.6839 38.5587 22 37.7956 22 37C22 35.335 20.65 34 19 34Z" fill="#3A3E3F"/>
            </g>
            <g clipPath="url(#clip1_2113_643)">
                <path d="M34 34C32.335 34 31 35.335 31 37C31 37.7956 31.3161 38.5587 31.8787 39.1213C32.4413 39.6839 33.2044 40 34 40C34.7956 40 35.5587 39.6839 36.1213 39.1213C36.6839 38.5587 37 37.7956 37 37C37 35.335 35.65 34 34 34ZM10 10V13H13L18.4 24.385L16.36 28.06C16.135 28.48 16 28.975 16 29.5C16 30.2956 16.3161 31.0587 16.8787 31.6213C17.4413 32.1839 18.2044 32.5 19 32.5H37V29.5H19.63C19.5305 29.5 19.4352 29.4605 19.3648 29.3902C19.2945 29.3198 19.255 29.2245 19.255 29.125C19.255 29.05 19.27 28.99 19.3 28.945L20.65 26.5H31.825C32.95 26.5 33.94 25.87 34.45 24.955L39.82 15.25C39.925 15.01 40 14.755 40 14.5C40 14.1022 39.842 13.7206 39.5607 13.4393C39.2794 13.158 38.8978 13 38.5 13H16.315L14.905 10M19 34C17.335 34 16 35.335 16 37C16 37.7956 16.3161 38.5587 16.8787 39.1213C17.4413 39.6839 18.2044 40 19 40C19.7956 40 20.5587 39.6839 21.1213 39.1213C21.6839 38.5587 22 37.7956 22 37C22 35.335 20.65 34 19 34Z" fill="#3A3E3F"/>
            </g>
            <defs>
                <clipPath id="clip0_2113_643">
                    <rect width="24" height="50" fill="white" transform="translate(24)"/>
                </clipPath>
                <clipPath id="clip1_2113_643">
                    <rect width="26" height="50" fill="white"/>
                </clipPath>
            </defs>
        </svg>
    )
};

export const DataManagementIcon = () => <DataManagementIconSingle sx={{color: "#FFFFFF"}}/>;
export const DataManagementIconActive = () => <DataManagementIconSingle sx={{color: "#3A3E3F"}}/>;

// Kept for possible future use:
// export const DataManagementIcon = RestoreIcon;
// export const DataManagementIconActive = RestoreIconActive;

export const SettingsIcon = () => {
    return (
        <svg width="37" height="36" viewBox="0 0 37 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M24.3135 27.675C26.3355 27.675 27.983 26.025 27.983 24C27.983 21.975 26.3355 20.325 24.3135 20.325C22.2766 20.325 20.6441 21.975 20.6441 24C20.6441 26.025 22.2766 27.675 24.3135 27.675ZM32.1017 25.02L34.3183 26.76C34.513 26.925 34.5729 27.195 34.4381 27.435L32.3413 31.065C32.2065 31.29 31.9519 31.38 31.6973 31.29L29.0913 30.24C28.5521 30.66 27.953 31.005 27.324 31.275L26.9196 34.05C26.8896 34.305 26.665 34.5 26.4104 34.5H22.2167C21.9471 34.5 21.7375 34.305 21.6925 34.05L21.3031 31.275C20.6591 31.005 20.075 30.66 19.5358 30.24L16.9148 31.29C16.6902 31.38 16.4056 31.29 16.2858 31.065L14.189 27.435C14.0542 27.195 14.1141 26.925 14.3088 26.76L16.5254 25.02L16.4505 24L16.5254 22.965L14.3088 21.24C14.1141 21.075 14.0542 20.79 14.189 20.565L16.2858 16.935C16.4056 16.695 16.6902 16.605 16.9148 16.695L19.5358 17.76C20.075 17.34 20.6591 16.98 21.3031 16.725L21.6925 13.935C21.7375 13.695 21.9471 13.5 22.2167 13.5H26.4104C26.665 13.5 26.8896 13.695 26.9196 13.935L27.324 16.725C27.953 16.98 28.5521 17.34 29.0913 17.76L31.6973 16.695C31.9519 16.605 32.2065 16.695 32.3413 16.935L34.4381 20.565C34.5729 20.79 34.513 21.075 34.3183 21.24L32.1017 22.965L32.1766 24L32.1017 25.02ZM10.5196 12.105C11.8226 12.105 12.871 11.055 12.871 9.75C12.871 8.445 11.8226 7.38 10.5196 7.38C9.89194 7.38 9.29004 7.6297 8.84626 8.07416C8.40247 8.51862 8.15315 9.12144 8.15315 9.75C8.15315 11.055 9.21654 12.105 10.5196 12.105ZM15.5219 10.41L16.9747 11.52C17.0796 11.625 17.1095 11.805 17.0197 11.955L15.6717 14.295C15.5968 14.445 15.4171 14.505 15.2673 14.445L13.5899 13.77L12.4816 14.43L12.197 16.215C12.167 16.38 12.0322 16.5 11.8675 16.5H9.1716C8.99188 16.5 8.85708 16.38 8.82713 16.215L8.58749 14.43L7.44923 13.77L5.74183 14.445C5.60703 14.505 5.44228 14.445 5.35242 14.295L4.00447 11.955C3.91461 11.805 3.95954 11.625 4.07936 11.52L5.50219 10.41L5.45726 9.75L5.50219 9.09L4.07936 7.98C3.95954 7.875 3.91461 7.695 4.00447 7.545L5.35242 5.205C5.44228 5.055 5.60703 4.995 5.74183 5.055L7.43425 5.73L8.58749 5.07L8.82713 3.285C8.85708 3.12 8.99188 3 9.1716 3H11.8675C12.0322 3 12.167 3.12 12.197 3.285L12.4816 5.07L13.5899 5.73L15.2673 5.055C15.4171 4.995 15.5968 5.055 15.6717 5.205L17.0197 7.545C17.1095 7.695 17.0796 7.875 16.9747 7.98L15.5219 9.09L15.5669 9.75L15.5219 10.41Z"
                fill="#CED3D9"/>
            <g clipPath="url(#clip0_2113_862)">
                <path
                    d="M24.3682 27.6438C26.3902 27.6438 28.0376 25.9937 28.0376 23.9687C28.0376 21.9437 26.3902 20.2937 24.3682 20.2937C22.3313 20.2937 20.6988 21.9437 20.6988 23.9687C20.6988 25.9937 22.3313 27.6438 24.3682 27.6438ZM32.1564 24.9887L34.373 26.7288C34.5677 26.8937 34.6276 27.1637 34.4928 27.4037L32.396 31.0337C32.2612 31.2587 32.0066 31.3487 31.752 31.2587L29.146 30.2087C28.6068 30.6287 28.0077 30.9737 27.3786 31.2437L26.9743 34.0187C26.9443 34.2738 26.7197 34.4687 26.465 34.4687H22.2714C22.0018 34.4687 21.7922 34.2738 21.7472 34.0187L21.3578 31.2437C20.7138 30.9737 20.1297 30.6287 19.5905 30.2087L16.9695 31.2587C16.7448 31.3487 16.4603 31.2587 16.3405 31.0337L14.2437 27.4037C14.1089 27.1637 14.1688 26.8937 14.3635 26.7288L16.5801 24.9887L16.5052 23.9687L16.5801 22.9338L14.3635 21.2087C14.1688 21.0437 14.1089 20.7587 14.2437 20.5337L16.3405 16.9037C16.4603 16.6637 16.7448 16.5737 16.9695 16.6637L19.5905 17.7288C20.1297 17.3088 20.7138 16.9487 21.3578 16.6937L21.7472 13.9037C21.7922 13.6637 22.0018 13.4687 22.2714 13.4687H26.465C26.7197 13.4687 26.9443 13.6637 26.9743 13.9037L27.3786 16.6937C28.0077 16.9487 28.6068 17.3088 29.146 17.7288L31.752 16.6637C32.0066 16.5737 32.2612 16.6637 32.396 16.9037L34.4928 20.5337C34.6276 20.7587 34.5677 21.0437 34.373 21.2087L32.1564 22.9338L32.2313 23.9687L32.1564 24.9887ZM10.5742 12.0737C11.8773 12.0737 12.9257 11.0237 12.9257 9.71875C12.9257 8.41375 11.8773 7.34875 10.5742 7.34875C9.94663 7.34875 9.34473 7.59845 8.90094 8.04291C8.45716 8.48737 8.20784 9.09019 8.20784 9.71875C8.20784 11.0237 9.27122 12.0737 10.5742 12.0737Z"
                    fill="#617388"/>
            </g>
            <defs>
                <clipPath id="clip0_2113_862">
                    <rect width="23.4192" height="23.4548" fill="white" transform="translate(13.0806 12.5137)"/>
                </clipPath>
            </defs>
        </svg>
    )
};

export const SettingsIconActive = () => {
    return (
        <svg width="37" height="36" viewBox="0 0 37 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M24.3135 27.675C26.3355 27.675 27.983 26.025 27.983 24C27.983 21.975 26.3355 20.325 24.3135 20.325C22.2766 20.325 20.6441 21.975 20.6441 24C20.6441 26.025 22.2766 27.675 24.3135 27.675ZM32.1017 25.02L34.3183 26.76C34.513 26.925 34.5729 27.195 34.4381 27.435L32.3413 31.065C32.2065 31.29 31.9519 31.38 31.6973 31.29L29.0913 30.24C28.5521 30.66 27.953 31.005 27.324 31.275L26.9196 34.05C26.8896 34.305 26.665 34.5 26.4104 34.5H22.2167C21.9471 34.5 21.7375 34.305 21.6925 34.05L21.3031 31.275C20.6591 31.005 20.075 30.66 19.5358 30.24L16.9148 31.29C16.6902 31.38 16.4056 31.29 16.2858 31.065L14.189 27.435C14.0542 27.195 14.1141 26.925 14.3088 26.76L16.5254 25.02L16.4505 24L16.5254 22.965L14.3088 21.24C14.1141 21.075 14.0542 20.79 14.189 20.565L16.2858 16.935C16.4056 16.695 16.6902 16.605 16.9148 16.695L19.5358 17.76C20.075 17.34 20.6591 16.98 21.3031 16.725L21.6925 13.935C21.7375 13.695 21.9471 13.5 22.2167 13.5H26.4104C26.665 13.5 26.8896 13.695 26.9196 13.935L27.324 16.725C27.953 16.98 28.5521 17.34 29.0913 17.76L31.6973 16.695C31.9519 16.605 32.2065 16.695 32.3413 16.935L34.4381 20.565C34.5729 20.79 34.513 21.075 34.3183 21.24L32.1017 22.965L32.1766 24L32.1017 25.02ZM10.5196 12.105C11.8226 12.105 12.871 11.055 12.871 9.75C12.871 8.445 11.8226 7.38 10.5196 7.38C9.89194 7.38 9.29004 7.6297 8.84626 8.07416C8.40247 8.51862 8.15315 9.12144 8.15315 9.75C8.15315 11.055 9.21654 12.105 10.5196 12.105ZM15.5219 10.41L16.9747 11.52C17.0796 11.625 17.1095 11.805 17.0197 11.955L15.6717 14.295C15.5968 14.445 15.4171 14.505 15.2673 14.445L13.5899 13.77L12.4816 14.43L12.197 16.215C12.167 16.38 12.0322 16.5 11.8675 16.5H9.1716C8.99188 16.5 8.85708 16.38 8.82713 16.215L8.58749 14.43L7.44923 13.77L5.74183 14.445C5.60703 14.505 5.44228 14.445 5.35242 14.295L4.00447 11.955C3.91461 11.805 3.95954 11.625 4.07936 11.52L5.50219 10.41L5.45726 9.75L5.50219 9.09L4.07936 7.98C3.95954 7.875 3.91461 7.695 4.00447 7.545L5.35242 5.205C5.44228 5.055 5.60703 4.995 5.74183 5.055L7.43425 5.73L8.58749 5.07L8.82713 3.285C8.85708 3.12 8.99188 3 9.1716 3H11.8675C12.0322 3 12.167 3.12 12.197 3.285L12.4816 5.07L13.5899 5.73L15.2673 5.055C15.4171 4.995 15.5968 5.055 15.6717 5.205L17.0197 7.545C17.1095 7.695 17.0796 7.875 16.9747 7.98L15.5219 9.09L15.5669 9.75L15.5219 10.41Z"
                fill="#3A3E3F"/>
            <g clipPath="url(#clip0_2113_827)">
                <path
                    d="M24.3682 27.6438C26.3902 27.6438 28.0376 25.9937 28.0376 23.9687C28.0376 21.9437 26.3902 20.2937 24.3682 20.2937C22.3313 20.2937 20.6988 21.9437 20.6988 23.9687C20.6988 25.9937 22.3313 27.6438 24.3682 27.6438ZM32.1564 24.9887L34.373 26.7288C34.5677 26.8937 34.6276 27.1637 34.4928 27.4037L32.396 31.0337C32.2612 31.2587 32.0066 31.3487 31.752 31.2587L29.146 30.2087C28.6068 30.6287 28.0077 30.9737 27.3786 31.2437L26.9743 34.0187C26.9443 34.2738 26.7197 34.4687 26.465 34.4687H22.2714C22.0018 34.4687 21.7922 34.2738 21.7472 34.0187L21.3578 31.2437C20.7138 30.9737 20.1297 30.6287 19.5905 30.2087L16.9695 31.2587C16.7448 31.3487 16.4603 31.2587 16.3405 31.0337L14.2437 27.4037C14.1089 27.1637 14.1688 26.8937 14.3635 26.7288L16.5801 24.9887L16.5052 23.9687L16.5801 22.9338L14.3635 21.2087C14.1688 21.0437 14.1089 20.7587 14.2437 20.5337L16.3405 16.9037C16.4603 16.6637 16.7448 16.5737 16.9695 16.6637L19.5905 17.7288C20.1297 17.3088 20.7138 16.9487 21.3578 16.6937L21.7472 13.9037C21.7922 13.6637 22.0018 13.4687 22.2714 13.4687H26.465C26.7197 13.4687 26.9443 13.6637 26.9743 13.9037L27.3786 16.6937C28.0077 16.9487 28.6068 17.3088 29.146 17.7288L31.752 16.6637C32.0066 16.5737 32.2612 16.6637 32.396 16.9037L34.4928 20.5337C34.6276 20.7587 34.5677 21.0437 34.373 21.2087L32.1564 22.9338L32.2313 23.9687L32.1564 24.9887ZM10.5742 12.0737C11.8773 12.0737 12.9257 11.0237 12.9257 9.71875C12.9257 8.41375 11.8773 7.34875 10.5742 7.34875C9.94663 7.34875 9.34473 7.59845 8.90094 8.04291C8.45716 8.48737 8.20784 9.09019 8.20784 9.71875C8.20784 11.0237 9.27122 12.0737 10.5742 12.0737Z"
                    fill="#3A3E3F"/>
            </g>
            <defs>
                <clipPath id="clip0_2113_827">
                    <rect width="23.4192" height="23.4548" fill="white" transform="translate(13.0806 12.5137)"/>
                </clipPath>
            </defs>
        </svg>
    )
};

export const TeamIcon = () => <TeamIconSingle sx={{color: "#CED3D9"}}/>;
export const TeamIconActive = () => <TeamIconSingle sx={{color: "#3A3E3F"}}/>;

export const CompanyIcon = () => <CustomerListIconSingle sx={{color: "#CED3D9"}}/>;
export const CompanyIconActive = () => <CustomerListIconSingle sx={{color: "#3A3E3F"}}/>;

export const JobIcon = () => <JobIconSingle sx={{color: "#CED3D9"}}/>;
export const JobIconActive = () => <JobIconSingle sx={{color: "#3A3E3F"}}/>;
